import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import axios from 'axios';
import { apiScopes } from '@/auth/authConfig';
import type { GuestUser, Role, StoreRef } from '@/types/user';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7071/api';

export interface UserState {
  user:      GuestUser | null;
  role:      Role | null;
  stores:    StoreRef[];
  isAdmin:   boolean;
  isOffice:  boolean;
  isStore:   boolean;
  seesAll:   boolean;
  /** true while /users/me is in flight (first load). */
  loading:   boolean;
  /** network/server failure loading /me — distinct from "signed out". */
  loadError: boolean;
}

const UserContext = createContext<UserState | null>(null);

/**
 * Loads GET /users/me ONCE for the whole app and shares it via context, so the
 * sidebar/home/gate don't each re-fetch. Consumed by useUserRole().
 */
export function UserProvider({ children }: { children: ReactNode }) {
  const { instance, accounts } = useMsal();
  const [user, setUser] = useState<GuestUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      setLoading(true);
      setLoadError(false);
      try {
        if (!accounts[0]) { setLoading(false); return; }
        const res = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
        const { data } = await axios.get<GuestUser>(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${res.accessToken}` },
        });
        if (!cancelled) setUser(data);
      } catch (err) {
        // SPA refresh tokens live 24h: a day-old window can't renew silently.
        // Redirect through Entra (SSO — no typing) instead of dead-ending on
        // "couldn't load your profile". Mirrors useApi's fallback.
        if (err instanceof InteractionRequiredAuthError && accounts[0]) {
          try {
            await instance.acquireTokenRedirect({ scopes: apiScopes, account: accounts[0] });
            return; // navigating away
          } catch { /* fall through to the error state */ }
        }
        if (!cancelled) { setLoadError(true); setUser(null); }
        console.error('UserProvider error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, [accounts, instance]);

  const role = user?.role ?? null;
  const value: UserState = {
    user,
    role,
    stores:   user?.stores ?? [],
    isAdmin:  role === 'admin',
    isOffice: role === 'office',
    isStore:  role === 'store',
    seesAll:  role === 'admin' || role === 'office',
    loading,
    loadError,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUserState(): UserState {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUserState must be used within a UserProvider');
  return ctx;
}
